"use client";

import React, { useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { 
  Instagram, Facebook, Linkedin, Globe, 
  ChevronLeft, ChevronRight
} from "lucide-react";

const subunitsData = [
  {
    id: 1,
    name: "CS",
    fullName: "Computer Society",
    logo: "/asset/logos/cswh.png",
    logoSizing: "w-[100px] h-[100px]",
    bannerImage: "/asset/banners/csg.webp",
    description:
      "The IEEE Computer Society (CS) students branch chapter at isims is focused on advancing the fields of computing and technology. It provides students with opportunities to participate in workshops, coding competitions, and technical talks aimed at enhancing their skills in computer science and software engineering. The chapter also encourages collaboration and learning through various projects.",
    social: { instagram: "https://www.instagram.com/ieee.isims.cs/", facebook: "https://www.facebook.com/profile.php?id=100088090993586", linkedin: "https://www.linkedin.com/company/ieee-computer-society-chapter-isims-student-branch/posts/?feedView=all", website: "https://cs-isims.ieee.tn/" },
    members: "450+",
    founded: "1985",
    location: "Main Campus",
  },
  {
    id: 2,
    name: "IAS",
    fullName: "Industrial Applications Society",
    logo: "/asset/logos/iasjdid.png",
    logoSizing: "w-12 h-12",
    bannerImage: "/asset/banners/iasg.webp",
    description:
      "The IEEE IAS students branch chapter at isims bridges the gap between students and the industry, focusing on applying engineering and scientific principles to solve industrial problems. Members have access to industry leaders and projects that provide real-world experience in power systems, automation, and industrial electronics.",
    social: { instagram: "https://www.instagram.com/ieee_ias_isims/", facebook: "https://www.facebook.com/profile.php?id=61572324778458", linkedin: "https://www.linkedin.com/company/ieee-ias-isims-sbc/posts/?feedView=all", website: "https://ias-isims.ieee.tn/" },
    members: "320+",
    founded: "1992",
    location: "Engineering Hall",
  },
  {
    id: 3,
    name: "IES-PES",
    fullName: "Power & Energy Society",
    logo: "/asset/logos/iespeswh.png",
    logoSizing: "w-12 h-12",
    bannerImage: "/asset/banners/iesg.webp",
    description:
      "The IEEE Industrial Electronics Society (IES) and Power and Energy Society (PES) students branch chapter at isims focuses on the application of electronics in industrial processes and the generation, transmission, and distribution of electrical energy. The chapter provides students with insights into power systems, renewable energy technologies, and industrial automation through workshops, seminars, and collaborative projects.",
    social: { instagram: "https://www.instagram.com/ieee.ies.pes.isims.sb.jc/", facebook: "https://www.facebook.com/profile.php?id=61580047484275", linkedin: "https://www.linkedin.com/company/ieee-ies-pes-student-branch-chapter-%E2%80%93-isims/posts/?feedView=all", website: "https://ies-pes-isims.ieee.tn/" },
    members: "280+",
    founded: "1990",
    location: "Energy Complex",
  },
  {
    id: 4,
    name: "WIE",
    fullName: "Women in Engineering",
    logo: "/asset/logos/wiejdid.png",
    logoSizing: "w-12 h-12",
    bannerImage: "/asset/banners/wieg.webp",
    description:
      "The Women in Engineering (WIE) affinity group at isims focuses on promoting gender diversity in engineering and technology. It provides a platform for female engineers to connect, collaborate, and contribute to the advancement of engineering fields. The group organizes workshops, mentoring programs, and events that encourage leadership and innovation among women in STEM.",
    social: { instagram: "https://www.instagram.com/ieee_wie_isims_sag/", facebook: "https://www.facebook.com/profile.php?id=100088426925351", linkedin: "https://www.linkedin.com/in/ieee-wie-isims-student-affinity-group-2a7a4035a/", website: "https://wie-isims.ieee.tn/" },
    members: "500+",
    founded: "2000",
    location: "Women in STEM Center",
  },
  {
    id: 5,
    name: "SIGHT",
    fullName: "Humanitarian Technology",
    logo: "/asset/logos/sightwh.png",
    logoSizing: "w-12 h-12",
    bannerImage: "/asset/banners/sightg.webp",
    description:
      "The IEEE SIGHT isims Student Branch Group was founded in October 2025, this SBG aims to promote IEEE Humanitarian Technologies Activities and SIGHT goals within IEEE isims SB Community. Activities are mainly focused on sustainable SDG oriented technical projects.",
    social: { instagram: "https://www.instagram.com/ieee.isims.sight.group/", facebook: "https://www.facebook.com/profile.php?id=61584359810922", linkedin: "#", website: "#" },
    members: "190+",
    founded: "2015",
    location: "Humanitarian Lab",
  },
  {
    id: 6,
    name: "SMC",
    fullName: "Systems, Man & Cybernetics",
    logo: "/asset/logos/smcwh.png",
    logoSizing: "w-12 h-12",
    bannerImage: "/asset/banners/smcg.webp",
    description:
      "The IEEE Systems, Man, and Cybernetics Society (SMC) students branch chapter at isims focuses on the interdisciplinary study of systems engineering, human-machine interaction, and intelligent systems. It provides students with opportunities to engage in activities such as technical workshops, innovation challenges, and research-oriented seminars covering topics like AI, cybernetics, and human-centered design.",
    social: { instagram: "https://www.instagram.com/ieee_smc_isims_sb_chapter/", facebook: "https://www.facebook.com/profile.php?id=61557150934003", linkedin: "https://www.linkedin.com/search/results/all/?keywords=smc%20isims&origin=GLOBAL_SEARCH_HEADER", website: "https://smc-isims.ieee.tn/" },
    members: "210+",
    founded: "2005",
    location: "Cybernetics Wing",
  },
  {
    id: 7,
    name: "RAS",
    fullName: "Robotics & Automation",
    logo: "/asset/logos/raswh.png",
    logoSizing: "w-12 h-12",
    bannerImage: "/asset/banners/rasg.webp",
    description:
      "The Robotics and Automation Society (RAS) students branch chapter at isims is a hub for students interested in the fields of robotics, automation, and artificial intelligence. The chapter organizes hands-on sessions where members can build and program robots, and participate in national and international robotics competitions.",
    social: { instagram: "https://www.instagram.com/ieee_ras_chapter_isims_sb/", facebook: "https://www.facebook.com/profile.php?id=100090530634047", linkedin: "linkedin.com/search/results/all/?keywords=ieee%20ras%20isims&origin=GLOBAL_SEARCH_HEADER", website: "https://ras-isims.ieee.tn/" },
    members: "380+",
    founded: "2008",
    location: "Robotics Lab",
  },
];

export default function Subunits() {
  const [activeUnit, setActiveUnit] = useState(subunitsData[0]);
  const scrollContainerRef = useRef(null);

  const handleUnitChange = (unit) => {
    setActiveUnit(unit);
    const buttonElement = document.getElementById(`unit-btn-${unit.id}`);
    if (buttonElement && scrollContainerRef.current) {
      const containerRect = scrollContainerRef.current.getBoundingClientRect();
      const buttonRect = buttonElement.getBoundingClientRect();
      if (buttonRect.left < containerRect.left || buttonRect.right > containerRect.right) {
        buttonElement.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
      }
    }
  };

  const nextUnit = () => {
    const currentIndex = subunitsData.findIndex(u => u.id === activeUnit.id);
    const nextIndex = (currentIndex + 1) % subunitsData.length;
    handleUnitChange(subunitsData[nextIndex]);
  };

  const prevUnit = () => {
    const currentIndex = subunitsData.findIndex(u => u.id === activeUnit.id);
    const prevIndex = (currentIndex - 1 + subunitsData.length) % subunitsData.length;
    handleUnitChange(subunitsData[prevIndex]);
  };

  return (
    <div id="subunits" className="relative border-t bg-white border-[#006699]/10 w-full py-12 px-4 sm:px-6 lg:px-8 overflow-hidden">
      
      {/* TEXTURE - Carreau background */}
      <div
        className="absolute inset-0"
        style={{
          backgroundImage: `
            repeating-linear-gradient(
              -45deg,
              rgba(15, 105, 157, 0.05) 0px,
              rgba(15, 105, 157, 0.05) 1px,
              transparent 1px,
              transparent 18px
            ),
            repeating-linear-gradient(
              45deg,
              rgba(15, 105, 157, 0.04) 0px,
              rgba(15, 105, 157, 0.04) 1px,
              transparent 1px,
              transparent 28px
            )
          `,
        }}
      />

      {/* Decorative Corner Elements - L borders */}
      <div className="absolute top-4 left-4 sm:top-8 sm:left-8 w-12 h-12 sm:w-24 sm:h-24 border-l-2 border-t-2 border-[#006699]/20" />
      <div className="absolute top-4 right-4 sm:top-8 sm:right-8 w-12 h-12 sm:w-24 sm:h-24 border-r-2 border-t-2 border-[#006699]/20" />
      <div className="absolute bottom-4 left-4 sm:bottom-8 sm:left-8 w-12 h-12 sm:w-24 sm:h-24 border-l-2 border-b-2 border-[#006699]/20" />
      <div className="absolute bottom-4 right-4 sm:bottom-8 sm:right-8 w-12 h-12 sm:w-24 sm:h-24 border-r-2 border-b-2 border-[#006699]/20" />

      {/* Decorative Floating Dots Pattern */}
      <div className="absolute top-1/4 right-8 md:right-16 opacity-30 pointer-events-none">
        <div className="grid grid-cols-3 gap-2">
          {[...Array(9)].map((_, i) => (
            <div key={i} className="w-1.5 h-1.5 rounded-none bg-[#006699]/40" />
          ))}
        </div>
      </div>

      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        transition={{ duration: 0.6 }} 
        className="relative max-w-5xl mx-auto"
      >
        {/* Header Section */}
        <div className="items-center mb-8 sm:mb-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-block"
          >
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-medium text-slate-800 tracking-tight">
              Discover Our <span className="text-[#006699] font-bold">Subunits</span>
            </h2>
            <div className="w-20 h-1 mt-3 bg-[#006699] mx-auto opacity-80" />
          </motion.div>
        </div>

        {/* Logo Navigation */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="mb-8 flex justify-center items-center gap-3 sm:gap-4"
        >
          {/* Left Arrow */}
          <motion.button
            onClick={prevUnit}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 sm:p-2.5 bg-white border border-[#006699]/20 text-[#006699] hover:bg-[#006699] hover:text-white transition-all duration-300 shadow-sm flex-shrink-0"
          >
            <ChevronLeft className="w-5 h-5" />
          </motion.button>

          {/* Scrollable Logos Container */}
          <div 
            ref={scrollContainerRef}
            className="bg-white/80 backdrop-blur-sm p-2 sm:p-3 shadow-sm border border-[#006699]/10 overflow-x-auto scrollbar-hide flex-1"
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
          >
            <div className="flex justify-center items-center gap-2 sm:gap-3 flex-nowrap min-w-max">
              {subunitsData.map((unit) => (
                <motion.button
                  key={unit.id}
                  id={`unit-btn-${unit.id}`}
                  onClick={() => handleUnitChange(unit)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`relative transition-all duration-300 flex flex-col items-center gap-1`}
                >
                  <div className={`
                    w-12 h-12 sm:w-14 sm:h-14 md:w-16 md:h-16 flex items-center justify-center
                    transition-all duration-300
                    ${activeUnit.id === unit.id 
                      ? "bg-[#006699] shadow-md shadow-[#006699]/20" 
                      : "bg-white border border-[#006699]/10 hover:border-[#006699]/30"
                    }
                  `}>
                    <img 
                      src={unit.logo} 
                      alt={`${unit.name} logo`}
                      className={`w-7 h-7 sm:w-8 sm:h-8 md:w-10 md:h-10 object-contain transition-all duration-300 ${
                        activeUnit.id === unit.id 
                          ? "brightness-0 invert" 
                          : "brightness-0 opacity-40 hover:opacity-60"
                      }`}
                    />
                  </div>
                  {/* Active indicator line instead of dot */}
                  {activeUnit.id === unit.id && (
                    <motion.div
                      layoutId="activeDot"
                      className="absolute -bottom-1.5 w-full h-1 bg-[#006699]"
                      transition={{ type: "spring", stiffness: 300, damping: 30 }}
                    />
                  )}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Right Arrow */}
          <motion.button
            onClick={nextUnit}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="p-2 sm:p-2.5 bg-white border border-[#006699]/20 text-[#006699] hover:bg-[#006699] hover:text-white transition-all duration-300 shadow-sm flex-shrink-0"
          >
            <ChevronRight className="w-5 h-5" />
          </motion.button>
        </motion.div>

        {/* Content Card - Sharp corners and smaller height */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeUnit.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="bg-white/90 backdrop-blur-md shadow-xl border-2 border-[#006699]/20 relative overflow-hidden flex flex-col md:flex-col rounded-none"
          >
            {/* L border accents on the card itself */}
            <div className="absolute top-0 left-0 w-8 h-8 border-l-2 border-t-2 border-[#006699]/30 z-10" />
            <div className="absolute top-0 right-0 w-8 h-8 border-r-2 border-t-2 border-[#006699]/30 z-10" />
            <div className="absolute bottom-0 left-0 w-8 h-8 border-l-2 border-b-2 border-[#006699]/30 z-10" />
            <div className="absolute bottom-0 right-0 w-8 h-8 border-r-2 border-b-2 border-[#006699]/30 z-10" />

            {/* Banner Image - Reduced height */}
            <div className="relative w-full h-[120px] sm:h-[150px] md:h-[200px] flex items-center justify-center bg-[#f5f5f5] overflow-hidden group">
              <img
                src={activeUnit.bannerImage}
                alt={`${activeUnit.name} banner`}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-black/5 pointer-events-none transition-colors duration-500 group-hover:bg-black/10" />

              {/* Centered Logo (White) - Reduced size */}
              <motion.img
                key={`logo-${activeUnit.id}`}
                src={activeUnit.logo}
                alt={`${activeUnit.name} logo`}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, type: "spring" }}
                className="absolute w-20 h-20 sm:w-28 sm:h-28 md:w-36 md:h-36 object-contain drop-shadow-2xl"
              />
            </div>

            {/* Social Icons - Tighter spacing */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="relative flex justify-center gap-2 -mt-5 z-10"
            >
              {activeUnit.social.instagram && activeUnit.social.instagram !== "#" && (
                <SocialIcon href={activeUnit.social.instagram} icon={<Instagram />} label="Instagram" />
              )}
              {activeUnit.social.facebook && activeUnit.social.facebook !== "#" && (
                <SocialIcon href={activeUnit.social.facebook} icon={<Facebook />} label="Facebook" />
              )}
              {activeUnit.social.linkedin && activeUnit.social.linkedin !== "#" && (
                <SocialIcon href={activeUnit.social.linkedin} icon={<Linkedin />} label="LinkedIn" />
              )}
              {activeUnit.social.website && activeUnit.social.website !== "#" && (
                <SocialIcon href={activeUnit.social.website} icon={<Globe />} label="Website" />
              )}
            </motion.div>

            {/* Content Details - Reduced padding */}
            <div className="p-4 sm:p-6 pt-3 sm:pt-4">
              <motion.div 
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.25 }}
                className="bg-white/50 p-3 sm:p-4 border-l-4 border-[#006699] relative"
              >
                <p className="text-slate-700 leading-snug font-medium text-xs sm:text-sm md:text-base relative z-10">
                  {activeUnit.description}
                </p>
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Clean Pagination Lines */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center gap-2 mt-6"
        >
          {subunitsData.map((unit) => (
            <button
              key={`dot-${unit.id}`}
              onClick={() => handleUnitChange(unit)}
              className={`transition-all duration-300 ${
                activeUnit.id === unit.id
                  ? "w-8 h-1.5 bg-[#006699]"
                  : "w-4 h-1.5 bg-[#006699]/20 hover:bg-[#006699]/40"
              }`}
              aria-label={`Go to ${unit.name}`}
            />
          ))}
        </motion.div>
      </motion.div>
    </div>
  );
}

// Cleaned up Social Icon Component - Sharp corners
function SocialIcon({ href, icon, label }) {
  return (
    <motion.a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      whileHover={{ scale: 1.1, y: -2 }}
      whileTap={{ scale: 0.95 }}
      className="group relative"
    >
      <div className="p-2 sm:p-2.5 bg-white border border-[#006699]/20 text-[#006699] transition-all duration-300 hover:shadow-md hover:bg-[#006699] hover:text-white">
        {React.cloneElement(icon, { 
          className: "w-4 h-4" 
        })}
      </div>
      
      {/* Tooltip */}
      <span className="absolute -top-8 left-1/2 -translate-x-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-[#006699] text-white text-[10px] py-1 px-2 whitespace-nowrap pointer-events-none shadow-sm z-20">
        {label}
      </span>
    </motion.a>
  );
}